import { Inject, Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DzModalService } from '@blk/daisy/modules/modals';
import { V1ExternalAccountInfo } from '@blk/portfolio-security-server/accountInfo';
import { AssetDetailsResponse } from '@ddt-web/common';
import { APP_STORE_CONFIG_TOKEN, ApplicationPathConstants } from '@ddt-web/config';
import { AppStoreConfig } from '@ddt-web/data-service';
import { Asset, UserDataJsonResponse, WebUserDataConstants } from '@ddt-web/domain';
import { HoldingsUtil } from '@ddt-web/generic-utils';
import { AssetResolveResponse, PasApiService } from '@ddt-web/pas';
import { AccountErrors, ApplicationStateStoreProxyService, ExistingExternalPortfolioMetaData, PartnerAccount, PartnerPlatform, PartnerPosition, PortfolioAssetDetailsResponse, PortfolioHoldingsResolutionStoreService, PortfolioHoldingsResolutionUtil } from '@ddt-web/shared-stores';
import { StoreService } from '@ddt-web/store-service';
import { PortfolioHoldingsResolutionRedirectOrigin, UrlQueryParamService } from '@ddt-web/url';
import { PartnerApiError, PartnerConstants } from '@ddt-web/user-data';
import { WweSecurityDetailsParams } from '@ddt-web/wealth-web';
import { WebToolsPlatformDataService } from '@ddt-web/web-tools-platform';
import * as uniqBy from 'lodash/uniqBy';
import { Observable, of, throwError, zip } from 'rxjs';
import { first, map, switchMap, take, withLatestFrom } from 'rxjs/operators';

import { PartnerPlatformConstants } from '../constants';
import { DirtyPortfolioDialogComponent } from '../partner-portfolios/dirty-portfolio-dialog/dirty-portfolio-dialog.component';
import { PartnerPortfolioValidatorService } from './partner-portfolio-validator.service';
import { PartnerPortfoliosDataService } from './partner-portfolios-data.service';

@Injectable({
	providedIn: 'root'
})
export class PartnerPortfolioImportService {
	private readonly maxHoldingPercentage: number = 75;
	private readonly cashAladdinId: string = 'XUSD00000';

	constructor(
		private partnerPortfoliosDataService: PartnerPortfoliosDataService,
		private webToolsPlatformDataService: WebToolsPlatformDataService,
		private pasApiService: PasApiService,
		private storeService: StoreService,
		private router: Router,
		private applicationStateStoreProxyService: ApplicationStateStoreProxyService,
		private portfolioHoldingsResolutionStoreService: PortfolioHoldingsResolutionStoreService,
		private partnerPortfolioValidatorService: PartnerPortfolioValidatorService,
		private dzModalService: DzModalService,
		private activatedRoute: ActivatedRoute,
		private urlQueryParamService: UrlQueryParamService,
		@Inject(APP_STORE_CONFIG_TOKEN) public storeConfig: AppStoreConfig
	) { }

	public fetchAccountAndAssetData(
		selectedPortfolioIds: string[],
		selectedPartnerId: string,
		rc: string
	): Observable<[AssetResolveResponse, PartnerAccount[]]> {
		return this.partnerPortfoliosDataService.getAccountsBatchData(selectedPartnerId, selectedPortfolioIds)
			.pipe(
				map((accounts: PartnerAccount[]) => {
					// Only include positions which have market value
					for (const account of accounts) {
						const filteredPositions = account.positions.filter(position => position.marketValue > 0);
						const hasMarketValueErrors = String(filteredPositions.length !== account.positions.length);
						account.metadata['hasMarketValueErrors'] = { stringValue: hasMarketValueErrors };
						account.positions = filteredPositions;
					}
					return accounts;
				}),
				switchMap((accounts: PartnerAccount[]) => {
					return zip(this.pasApiService.fetchSecurityDetails(this.buildAssetResolveRequestForAccounts(accounts, rc)), of(accounts));
				})
			);
	}
	/**
	 * build the partner portfolios and save
	 *
	 * @param {V1FilterPlatformsRequest} resolvedAssets AssetResolveResponse
	 * @param {V1FilterPlatformsRequest} selectedPortfolios a Map of account id to portfolio name or account id to unique id(incase of portfolio update)			
	 * @param {V1FilterPlatformsRequest} accounts PartnerAccount
	 * @param {V1FilterPlatformsRequest} selectedPartnerId partnerId
	 * @returns {Observable<UserDataJsonResponse>}
	 * @memberof PartnerPortfolioImportService
	 */
	public savePartnerPortfolios(
		resolvedAssets: AssetResolveResponse,
		selectedPortfolios: Map<string, V1ExternalAccountInfo>,
		accounts: PartnerAccount[],
		selectedPartnerId: string,
		existingPortfolioDataByAccountId: Map<string, ExistingExternalPortfolioMetaData>
	): Observable<UserDataJsonResponse[]> {
		const portfoliosToSave = this.buildPortfoliosToSave(accounts, resolvedAssets, selectedPartnerId, selectedPortfolios, existingPortfolioDataByAccountId);
		return zip(...portfoliosToSave.map(portfolio => this.webToolsPlatformDataService.savePortfolioToAPI(portfolio)));
	}

	/**
	 * build the partner portfolios and save
	 *
	 * @param {V1FilterPlatformsRequest} resolvedAssets AssetResolveResponse
	 * @param {V1FilterPlatformsRequest} accounts PartnerAccount
	 * 
	 * @returns {Observable<UserDataJsonResponse>}
	 * @memberof PartnerPortfolioImportService
	 */
	public updatePartnerPortfolios(
		resolvedAssets: AssetResolveResponse,
		accounts: PartnerAccount[]
	): Observable<UserDataJsonResponse[]> {
		const portfoliosToUpdate = this.buildPortfoliosToUpdate(accounts, resolvedAssets);
		return zip(...portfoliosToUpdate.map(portfolio => this.webToolsPlatformDataService.savePortfolioToAPI(portfolio)));
	}

	public saveResolvedPartnerPortfolios(
		partnerPortfoliosWithAssetDetails: PortfolioAssetDetailsResponse[],
		selectedPartner: string,
		errorPortfolioIds: string[]
	): Observable<UserDataJsonResponse[]> {
		return zip(...(partnerPortfoliosWithAssetDetails.map(
			portfolio => this.webToolsPlatformDataService.searchPortfolios(
				portfolio.accountName
			)
		)))
			.pipe(
				first(),
				withLatestFrom(this.storeService.selectAll(this.storeConfig['userPortfolios'])),
				switchMap(([portfolioNameResults, userPortfolios]) => {
					const existingPortfolioDataByAccountId = PortfolioHoldingsResolutionUtil.aggregateExistingPortfolioDataByPartnerAccountId(
						partnerPortfoliosWithAssetDetails,
						userPortfolios.entities,
						selectedPartner
					);

					const portfolioHasErrorByIndex = portfolioNameResults.map((nameResult, i) => {
						let hasError: boolean = false;
						if (nameResult.length) {
							if ([...existingPortfolioDataByAccountId.values()][i]?.name !== nameResult[0].description) {
								hasError = true;
							}
						}
						return hasError;
					});

					return zip(of(portfolioHasErrorByIndex.some((portfolioHasError) => portfolioHasError === true)), of(existingPortfolioDataByAccountId));
				}),
				switchMap(([doDuplicatePortfolioNamesExist, existingPortfolioDataByAccountId]) => {
					if (doDuplicatePortfolioNamesExist) {
						return throwError({ error: PartnerConstants.PARTNER.DUPLICATE_PORTFOLIO_NAME_ERROR } as PartnerApiError);
					} else {
						const portfoliosToSave = partnerPortfoliosWithAssetDetails.map(portfolio => {
							const children = portfolio.holdings?.map(position => {
								const assetInfo = position.assets[0];
								const identifierInfo = HoldingsUtil.generateIdentifierAndIdentifierType(assetInfo);
								const childIdentifiers = HoldingsUtil.retrieveImportantSecurityIdentifiers(assetInfo, ['aladdinId']);
								return {
									identifier: identifierInfo.identifier,
									identifierType: identifierInfo.identifierType,
									value: position.value,
									subType: WebUserDataConstants.CHILD_SUBTYPE.HOLDING,
									...(position.substitutedFrom && { substitutedFrom: position.substitutedFrom }),
									...childIdentifiers
								};
							});
							const portfolioToSave = {
								description: portfolio.accountName,
								type: WebUserDataConstants.TYPE.PORTFOLIO,
								subType: WebUserDataConstants.SUBTYPE.EXTERNAL_PORTFOLIO,
								children: children,
								encrypted: {
									originPartnerAccountId: portfolio.accountId
								},
								originPlatform: {
									platform: portfolio.partnerId
								},
								perms: WebUserDataConstants.USER_DATA_PERMS,
								flags: {
									isProcessed: errorPortfolioIds.includes(portfolio.accountId) // must check as some of the portfolios being saved may not have errors
								}
							};

							return this.enrichPortfolioWithUniqueIdIfExists(portfolio.accountId, portfolioToSave, existingPortfolioDataByAccountId);
						});
						return zip(...portfoliosToSave.map(portfolio => this.webToolsPlatformDataService.savePortfolioToAPI(portfolio)));
					}
				})
			);
	}

	public validatePortfolioNamesAndSavePartnerPortfolios(
		selectedPortfolios: Map<string, V1ExternalAccountInfo>,
		accountErrorMap: Map<string, AccountErrors>,
		accounts: PartnerAccount[],
		resolvedAssets: AssetResolveResponse,
		selectedPartner: PartnerPlatform,
		urlIds: string[] = [],
		isCompareMode: boolean = false
	): Observable<UserDataJsonResponse[]> {
		return zip(...([...selectedPortfolios.values()].map(
			portfolio => this.webToolsPlatformDataService.searchPortfolios(
				portfolio.additionalAttributes?.[PartnerPlatformConstants.MODIFIED_DISPLAY_NAME_FIELD_NAME]?.stringValue ??
				portfolio.name
			)
		)))
			.pipe(
				first(),
				withLatestFrom(this.storeService.selectAll(this.storeConfig['userPortfolios'])),
				switchMap(([portfolioNameResults, userPortfolios]) => {
					const existingPortfolioDataByAccountId = PortfolioHoldingsResolutionUtil.aggregateExistingPortfolioDataByPartnerAccountId(
						[...selectedPortfolios.values()],
						userPortfolios.entities,
						selectedPartner.id
					);

					const portfolioHasErrorByIndex = portfolioNameResults.map((nameResult, i) => {
						let hasError: boolean = false;
						if (nameResult.length) {
							if ([...existingPortfolioDataByAccountId.values()][i]?.name !== nameResult[0].description) {
								hasError = true;
							}
						}

						[...selectedPortfolios.values()][i].additionalAttributes[PartnerPlatformConstants.MODIFIED_DISPLAY_NAME_HAS_ERRORS] = { stringValue: hasError.toString() };
						if (hasError) {
							const modifiedDisplayName = [...selectedPortfolios.values()][i]
								.additionalAttributes[PartnerPlatformConstants.MODIFIED_DISPLAY_NAME_FIELD_NAME]?.stringValue;
							if (!modifiedDisplayName) {
								[...selectedPortfolios.values()][i]
									.additionalAttributes[PartnerPlatformConstants.MODIFIED_DISPLAY_NAME_FIELD_NAME] = { stringValue: nameResult[0].description };
							}
						}

						return hasError;
					});

					this.applicationStateStoreProxyService.updateImportPartnerPortfoliosSelectedPortfolios(selectedPortfolios);

					return zip(of(portfolioHasErrorByIndex.some((portfolioHasError) => portfolioHasError === true)), of(existingPortfolioDataByAccountId));
				}),
				switchMap(([doDuplicatePortfolioNamesExist, existingPortfolioDataByAccountId]) => {
					if (doDuplicatePortfolioNamesExist) {
						return throwError({ error: PartnerConstants.PARTNER.DUPLICATE_PORTFOLIO_NAME_ERROR } as PartnerApiError);
					} else {
						const accountIds = Array.from(selectedPortfolios.keys());
						if (accountIds.some(accountId => accountErrorMap.get(accountId).containsUnresolvedSecurities)) {
							this.handleHoldingsResolution(
								selectedPortfolios,
								accounts,
								resolvedAssets,
								accountErrorMap,
								selectedPartner,
								PortfolioHoldingsResolutionRedirectOrigin.PARTNER_IMPORT_DIALOG,
								urlIds,
								false,
								isCompareMode
							);
							return of(null);
						} else {
							return this.savePartnerPortfolios(
								resolvedAssets,
								selectedPortfolios,
								accounts,
								selectedPartner.id,
								existingPortfolioDataByAccountId
							);
						}
					}
				})
			);
	}

	/**
	 * refresh partner portfolios in deep link flow
	 *
	 * @param {string[]} partnerPortfolios partner portfolios
	 * @param {PartnerPlatform} selectedPartnerId partner id
	 * @param {string} rc rc
	 * @param {string[]} currentPortfolioIdsInAnalysis list of other portfolio ids in deep link
	 * 
	 * @returns {Observable<UserDataJsonResponse[]>}
	 * @memberof PartnerPortfolioImportService
	 */
	public refreshPartnerPortfolioDeepLink(
		partnerPortfolios: any[],
		selectedPartner: PartnerPlatform,
		rc: string,
		currentPortfolioIdsInAnalysis?: string[]
	): Observable<UserDataJsonResponse[]> {
		return this.fetchAccountAndAssetData(
			partnerPortfolios.map(portfolio => portfolio.encrypted.originPartnerAccountId),
			selectedPartner.id,
			rc
		).pipe(
			take(1),
			switchMap(([resolvedAssets, accounts]) => {
				if (!accounts.length) {
					// throw an error if batchGet response is empty
					throw new Error();
				} else {
					if (accounts.length !== partnerPortfolios.length) {
						// handle partial data response from batchGet
						const successfulPortfolioDataIds = accounts.map(account => account.id);
						const portfoliosWithMissingData = partnerPortfolios.filter(
							partnerPortfolio => !successfulPortfolioDataIds.includes(partnerPortfolio.encrypted.originPartnerAccountId)
						);
						const portfolioIdsWithMissingData = portfoliosWithMissingData.map(portfolioWithMissingData => portfolioWithMissingData.id);

						// remove portfolios with missing data from analysis, and update the url portfolioIds
						partnerPortfolios = partnerPortfolios.filter(partnerPortfolio => successfulPortfolioDataIds.includes(partnerPortfolio.encrypted.originPartnerAccountId));
						const updatedUrlIds = [...currentPortfolioIdsInAnalysis, ...partnerPortfolios.map(partnerPortfolio => partnerPortfolio.id)];
						const queryParams = this.urlQueryParamService.transformObjectToParams(
							Object.assign({}, { portfolios: updatedUrlIds }, { portfolioIdsWithMissingData })
						);

						this.router.navigate(
							[],
							{
								relativeTo: this.activatedRoute,
								queryParams,
								queryParamsHandling: 'merge'
							}
						);
					}

					const updatedAccounts = accounts.map(account => this.enrichAccountWithUniqueId(partnerPortfolios, account));
					const accountErrorMap = this.partnerPortfolioValidatorService.validatePortfoliosAndThrowErrors(updatedAccounts, resolvedAssets, partnerPortfolios, 'NEW', true);

					const accountIds = updatedAccounts.map(account => account.id);

					if (accountIds.some(accountId => accountErrorMap.get(accountId).hasPortfolioErrors)) {
						return of(null);
					}
					// If some securities are unresolved then update store and redirect to holdings resolution
					if (accountIds.some(accountId => accountErrorMap.get(accountId).containsUnresolvedSecurities)) {
						const selectedPartnerPortfolios = new Map();

						partnerPortfolios.forEach((portfolio) => {
							selectedPartnerPortfolios.set(portfolio.encrypted.originPartnerAccountId, { name: portfolio.description });
						});

						this.applicationStateStoreProxyService.updateImportPartnerPortfoliosState({
							selectedPortfolios: selectedPartnerPortfolios,
							partnerPlatform: selectedPartner
						});

						this.handleHoldingsResolution(
							selectedPartnerPortfolios,
							updatedAccounts,
							resolvedAssets,
							accountErrorMap,
							selectedPartner,
							null,
							currentPortfolioIdsInAnalysis,
							true
						);
						return of(null);
					} else {
						return this.updatePartnerPortfolios(resolvedAssets, updatedAccounts);
					}
				}
			})
		);
	}

	public enrichAccountWithUniqueId(selectedPartnerPortfolios: any[], account: PartnerAccount): PartnerAccount {
		return {
			...account,
			metadata: {
				...account.metadata,
				uniqueId: {
					stringValue: selectedPartnerPortfolios.find(portfolio =>
						portfolio.encrypted.originPartnerAccountId === account.id).uniqueId?.replace(PartnerConstants.PARTNER.EXT_IDENTIFIER, '')
				}
			}
		};
	}

	public handleHoldingsResolution(
		selectedPortfolios: Map<string, V1ExternalAccountInfo>,
		accounts: PartnerAccount[],
		resolvedAssets: AssetResolveResponse,
		accountErrorMap: Map<string, AccountErrors>,
		selectedPartner: PartnerPlatform,
		portfolioHoldingsResolutionRedirectOrigin,
		portfolioUpdateSkipList: string[] = [],
		isRefreshFlow: boolean = false,
		isCompareMode: boolean = false
	): void {
		const routeOrigin = this.router.url.startsWith(`/analysis/${ApplicationPathConstants.PORTFOLIO_ANALYSIS_PATHS.OVERVIEW}`)
			? `analysis/${ApplicationPathConstants.PORTFOLIO_ANALYSIS_PATHS.OVERVIEW}` : ApplicationPathConstants.NEW_ANALYSIS_PAGE_PATH;
		const assetDetailsResponses = this.buildPortfolioAssetDetailsResponses(selectedPortfolios, accounts, resolvedAssets, selectedPartner);
		this.updateAssetDetailsResponses(assetDetailsResponses);

		const configData = {
			showClose: true,
			style: {
				'background-color': 'white'
			},
			size: 'sm',
			contentData: {
				accountErrorMap: accountErrorMap,
				assetDetailsResponses,
				previousState: isRefreshFlow ? {} : {
					redirectOrigin: portfolioHoldingsResolutionRedirectOrigin,
					routeOrigin: routeOrigin,
					isCompareMode
				},
				portfolioUpdateSkipList,
				isRefreshFlow
			}
		};
		this.dzModalService.open(DirtyPortfolioDialogComponent, configData);
	}

	private updateAssetDetailsResponses(assetDetailsResponses: PortfolioAssetDetailsResponse[]): void {
		this.portfolioHoldingsResolutionStoreService.setAllPortfolioAssetDetailsResponses(assetDetailsResponses);
	}

	private buildPortfolioAssetDetailsResponses(
		selectedPortfolios: Map<string, V1ExternalAccountInfo>,
		accounts: PartnerAccount[],
		resolvedAssets: AssetResolveResponse,
		selectedPartner: PartnerPlatform
	): PortfolioAssetDetailsResponse[] {
		return accounts.map(account => {
			const portfolio = selectedPortfolios.get(account.id);
			const accountName = portfolio?.additionalAttributes?.[PartnerPlatformConstants.MODIFIED_DISPLAY_NAME_FIELD_NAME]?.stringValue ?? portfolio?.name;
			const holdings = account.positions.map(position => this.buildAssetDetailsResponse(position, resolvedAssets));
			return {
				requestId: account.id,
				accountId: account.id,
				partnerId: selectedPartner.id,
				accountName,
				holdings
			};
		});
	}

	private buildPortfoliosToUpdate(
		accounts: PartnerAccount[],
		resolvedAssets: AssetResolveResponse
	): any[] {
		return accounts.map(account => {
			const children = this.buildChildren(account, resolvedAssets);
			return {
				children: children,
				uniqueId: account.metadata.uniqueId.stringValue,
				flags: {
					isProcessed: false
				}
			};
		});
	}

	private buildAssetDetailsResponse(position: PartnerPosition, resolvedAssets: AssetResolveResponse): AssetDetailsResponse {
		const asset = resolvedAssets.assets.get(position.assetId) as Asset;
		const identifiers = new Map();
		identifiers.set('ticker', position.posInfo?.ticker?.stringValue);
		identifiers.set('cusip', position.posInfo?.cusip?.stringValue);
		return {
			requestId: position.assetId,
			name: position.posInfo?.securityName?.stringValue,
			value: position.marketValue,
			assets: asset ? [asset] : [],
			identifiers,
			hasErrors: !asset
		};
	}

	private buildPortfoliosToSave(
		accounts: PartnerAccount[],
		resolvedAssets: AssetResolveResponse,
		selectedPartnerId: string,
		selectedPortfolios: Map<string, V1ExternalAccountInfo>,
		existingPortfolioDataByAccountId: Map<string, ExistingExternalPortfolioMetaData>
	): any[] {
		return accounts.map(account => {
			const children = this.buildChildren(account, resolvedAssets);
			const portfolio = selectedPortfolios.get(account.id);
			const portfolioToSave = {
				description:
					portfolio.additionalAttributes?.[PartnerPlatformConstants.MODIFIED_DISPLAY_NAME_FIELD_NAME]?.stringValue ??
					existingPortfolioDataByAccountId.get(portfolio.id)?.name ??
					portfolio.name,
				type: WebUserDataConstants.TYPE.PORTFOLIO,
				subType: WebUserDataConstants.SUBTYPE.EXTERNAL_PORTFOLIO,
				children: children,
				encrypted: {
					originPartnerAccountId: account.id
				},
				originPlatform: {
					platform: selectedPartnerId
				},
				perms: WebUserDataConstants.USER_DATA_PERMS,
				flags: {
					isProcessed: false // this method is only used by the happy flow so this variable is always false
				}
			};

			return this.enrichPortfolioWithUniqueIdIfExists(portfolio.id, portfolioToSave, existingPortfolioDataByAccountId);
		});
	}

	private buildChildren(account: PartnerAccount, resolvedAssets: AssetResolveResponse) {
		return account.positions.map(position => {
			const assetInfo = resolvedAssets.assets.get(position.assetId);
			const identifierInfo = HoldingsUtil.generateIdentifierAndIdentifierType(assetInfo);
			const childIdentifiers = HoldingsUtil.retrieveImportantSecurityIdentifiers(assetInfo, ['aladdinId']);
			return {
				identifier: identifierInfo.identifier,
				identifierType: identifierInfo.identifierType,
				value: position.marketValue,
				subType: WebUserDataConstants.CHILD_SUBTYPE.HOLDING,
				...childIdentifiers
			};
		});
	}

	private buildAssetResolveRequestForAccounts(accounts: PartnerAccount[], rc: string): WweSecurityDetailsParams {
		const securities = [];
		accounts.forEach(account => {
			account.positions.forEach(position => {
				if (position.assetId !== 'XUNKNOWNX') {
					securities.push({
						id: position.assetId,
						identifiers: { aladdinId: position.assetId }
					});
				}
			});
		});

		return {
			body: {
				securities: uniqBy(securities, 'id')
			},
			rc
		} as WweSecurityDetailsParams;
	}

	private enrichPortfolioWithUniqueIdIfExists<T>(
		externalAccountId: string,
		portfolioToSave: T,
		existingPortfolioDataByAccountId: Map<string, ExistingExternalPortfolioMetaData>
	): T {
		return {
			...existingPortfolioDataByAccountId.get(externalAccountId) && { uniqueId: existingPortfolioDataByAccountId.get(externalAccountId).uniqueId },
			...portfolioToSave
		};
	}
}
